// ═══════════════════════════════════════════════════════════════════════════════
// In-app notification bell + dropdown (Phase 1)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NOTIFICATION_TYPE, USER_ROLE, appendInventoryOpenExpiringQuery, type Notification, type UserProfile } from '@fdh/shared';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { syncAppBadge } from '../lib/appBadge';
import { notificationApi } from '../lib/api';
import { getPostAuthHomePath } from '../lib/authRedirect';
import { buildHelperHomeStockOverviewPath } from '../lib/helperHomeStockOverview';

const POLL_MS = 60_000;
const LIST_PAGE_SIZE = 15;

const getNotificationHref = (n: Notification, user: UserProfile): string => {
  const { relatedType, relatedId } = n;
  if (n.type === NOTIFICATION_TYPE.MEMBERSHIP_DOWNGRADED) {
    return '/settings/usage';
  }
  if (
    n.type === NOTIFICATION_TYPE.SUBSCRIPTION_PAYMENT_FAILED ||
    n.type === NOTIFICATION_TYPE.SUBSCRIPTION_EXPIRING
  ) {
    return '/settings/subscription';
  }
  if (!relatedId) return getPostAuthHomePath(user);
  if (
    n.type === NOTIFICATION_TYPE.TASK_CREATED_FOR_HELPER ||
    n.type === NOTIFICATION_TYPE.TASK_UPDATED_FOR_HELPER
  ) {
    return '/today';
  }
  if (relatedType === 'task') return '/today';
  if (relatedType === 'task_instance') {
    if (
      user.role === USER_ROLE.HELPER &&
      n.type === NOTIFICATION_TYPE.COMMENT_ADDED
    ) {
      return buildHelperHomeStockOverviewPath(relatedId);
    }
    const basePath = `/task-instances/${relatedId}`;
    if (n.type === NOTIFICATION_TYPE.INVENTORY_EXPIRY) {
      return appendInventoryOpenExpiringQuery(basePath);
    }
    return basePath;
  }
  if (relatedType === 'receipt') return `/receipts/${relatedId}`;
  if (relatedType === 'pairing') return '/pairing/status';
  if (relatedType === 'settlement') {
    return `/settlements/${relatedId}`;
  }
  if (relatedType === 'budget') {
    return '/settlements';
  }
  if (relatedType === 'pin_message') {
    return `/home?pinMessageId=${relatedId}`;
  }
  if (relatedType === 'system_message' || n.type === NOTIFICATION_TYPE.SYSTEM_MESSAGE) {
    return `/system-messages?id=${relatedId ?? ''}`;
  }
  if (relatedType === 'subscription' || relatedType === 'membership_subscription_expiry') {
    return '/settings/subscription';
  }
  return getPostAuthHomePath(user);
};

type NotificationBellProps = {
  /** Slightly larger bell for AppTopBar (+10%) while keeping the 56px header row height. */
  size?: 'default' | 'header';
};

const BELL_SIZE_CLASSES = {
  default: { button: 'h-9 w-9', icon: 'h-6 w-6' },
  header: { button: 'h-[39.6px] w-[39.6px]', icon: 'h-[26.4px] w-[26.4px]' },
} as const;

const EXPAND_BODY_BADGE_CLASS =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-1.5 py-px text-[10px] font-medium leading-tight text-primary-900 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:border-primary-700 dark:bg-primary-950/40 dark:text-primary-100 dark:hover:bg-primary-900/60';

type NotificationListItemProps = {
  notification: Notification;
  onOpen: (notification: Notification) => void;
  onOpenKeyDown: (e: React.KeyboardEvent, notification: Notification) => void;
};

const NotificationListItem = ({
  notification,
  onOpen,
  onOpenKeyDown,
}: NotificationListItemProps) => {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    const measureTruncation = () => {
      if (isExpanded) {
        return;
      }
      setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    };
    measureTruncation();
    const observer = new ResizeObserver(() => {
      measureTruncation();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isExpanded, notification.body]);

  const showExpandToggle = isTruncated || isExpanded;

  const handleToggleExpandClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((previousExpanded) => !previousExpanded);
  };

  const handleToggleExpandKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(notification)}
        onKeyDown={(e) => onOpenKeyDown(e, notification)}
        className={
          notification.read
            ? 'cursor-pointer px-3 py-3 text-left transition hover:bg-gray-50 focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400 dark:hover:bg-gray-800 dark:focus:bg-gray-800'
            : 'cursor-pointer border-l-4 border-primary-500 bg-primary-50/50 px-3 py-3 text-left transition hover:bg-primary-50 focus:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-400 dark:border-primary-300 dark:bg-primary-900/35 dark:hover:bg-primary-900/50 dark:focus:bg-primary-900/50'
        }
      >
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{notification.title}</p>
        <p
          ref={bodyRef}
          className={
            isExpanded
              ? 'mt-0.5 text-xs text-gray-600 dark:text-gray-300'
              : 'mt-0.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-300'
          }
        >
          {notification.body}
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="min-w-0 text-[10px] text-gray-400 dark:text-gray-500">{notification.createdAt}</p>
          {showExpandToggle ? (
            <button
              type="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded ? t('notification.collapseAria') : t('notification.expandAria')
              }
              onClick={handleToggleExpandClick}
              onKeyDown={handleToggleExpandKeyDown}
              className={EXPAND_BODY_BADGE_CLASS}
            >
              {isExpanded ? t('notification.collapse') : t('notification.expand')}
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
};

export const NotificationBell = ({ size = 'default' }: NotificationBellProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await notificationApi.unreadCount();
      if (res.success && res.data) {
        setUnreadCount(res.data.count);
      }
    } catch {
      // ignore
    }
  }, []);

  const markAllReadSilently = useCallback(async () => {
    try {
      await notificationApi.markAllRead();
      await fetchUnread();
    } catch {
      // ignore
    }
  }, [fetchUnread]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await notificationApi.list(1, LIST_PAGE_SIZE);
      if (res.success && res.data) {
        setItems(res.data.notifications);
        setUnreadCount(res.data.unreadCount);
      } else {
        setListError(res.error ?? t('notification.loadError'));
      }
    } catch {
      setListError(t('notification.loadError'));
    } finally {
      setListLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUnread();
    const interval = window.setInterval(fetchUnread, POLL_MS);
    return () => window.clearInterval(interval);
  }, [fetchUnread]);

  useEffect(() => {
    void syncAppBadge(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    const onFocus = () => {
      fetchUnread();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchUnread]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen((previousOpen) => {
          if (previousOpen) {
            void markAllReadSilently();
          }
          return false;
        });
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [markAllReadSilently]);

  useEffect(() => {
    if (open) {
      fetchList();
    }
  }, [open, fetchList]);

  const handleToggle = () => {
    setOpen((previousOpen) => {
      if (previousOpen) {
        void markAllReadSilently();
      }
      return !previousOpen;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
    if (e.key === 'Escape') {
      void markAllReadSilently();
      setOpen(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const res = await notificationApi.markAllRead();
      if (res.success) {
        await fetchList();
        await fetchUnread();
      }
    } catch {
      setListError(t('notification.loadError'));
    }
  };

  const handleNotificationClick = async (n: Notification) => {
    if (!user) return;
    try {
      if (!n.read) await notificationApi.markRead(n.id);
      await notificationApi.markAllRead();
    } catch {
      // still navigate
    }
    setOpen(false);
    navigate(getNotificationHref(n, user), {
      state: { from: `${location.pathname}${location.search}` },
    });
    fetchUnread();
  };

  const handleNotificationKeyDown = (
    e: React.KeyboardEvent,
    n: Notification
  ) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleNotificationClick(n);
    }
  };

  const badge =
    unreadCount > 99 ? '99+' : unreadCount > 0 ? String(unreadCount) : '';
  const sizeClasses = BELL_SIZE_CLASSES[size];

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={t('notification.bellAria')}
        aria-expanded={open}
        aria-haspopup="true"
        className={`relative flex shrink-0 items-center justify-center rounded-lg text-gray-600 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800 ${sizeClasses.button}`}
      >
        <svg
          className={sizeClasses.icon}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {badge ? (
          <span
            className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            aria-hidden
          >
            {badge}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 z-50 mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
          role="dialog"
          aria-label={t('notification.panelTitle')}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('notification.panelTitle')}
            </span>
            <button
              type="button"
              onClick={handleMarkAllRead}
              tabIndex={0}
              aria-label={t('notification.markAllReadAria')}
              className="rounded px-1 text-xs font-medium text-primary-600 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-400 dark:text-primary-300 dark:hover:text-primary-200"
            >
              {t('notification.markAllRead')}
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {listLoading ? (
              <div className="flex justify-center py-8">
                <div className="h-7 w-7 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              </div>
            ) : null}
            {!listLoading && listError ? (
              <p className="px-3 py-6 text-center text-sm text-red-600" role="alert">
                {listError}
              </p>
            ) : null}
            {!listLoading && !listError && items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                {t('notification.empty')}
              </p>
            ) : null}
            {!listLoading && !listError && items.length > 0 ? (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700" role="list">
                {items.map((n) => (
                  <NotificationListItem
                    key={n.id}
                    notification={n}
                    onOpen={handleNotificationClick}
                    onOpenKeyDown={handleNotificationKeyDown}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
